/**
Copyright (c) 2022-2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  CubeIcon,
  CpuChipIcon,
  ServerStackIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  CommandLineIcon,
  GlobeAltIcon,
  CurrencyRupeeIcon,
  ExclamationTriangleIcon,
  ComputerDesktopIcon,
  KeyIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import Header from '../components/layout/Header';
import VMWebTerminal from '../components/vms/VMWebTerminal';
import VMLogs from '../components/vms/VMLogs';
import VMStats from '../components/vms/VMStats';
import VMFirewallPorts from '../components/vms/VMFirewallPorts';
import { vmApi } from '../services/vmApi';
import { sshKeyService } from '../services/api';
import { VM, VMStatus } from '../types/vm';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { AxiosError } from 'axios';

const statusColors: Record<VMStatus, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-600',
  creating: 'bg-blue-500/20 text-blue-400 border-blue-600',
  running: 'bg-green-500/20 text-green-400 border-green-600',
  stopping: 'bg-orange-500/20 text-orange-400 border-orange-600',
  stopped: 'bg-gray-700 text-gray-300 border-gray-600',
  error: 'bg-red-500/20 text-red-400 border-red-600',
  deleting: 'bg-red-500/20 text-red-400 border-red-600',
  deleted: 'bg-gray-700 text-gray-400 border-gray-600',
};

const VMDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [showResizeModal, setShowResizeModal] = useState(false);
  const [resizeCpu, setResizeCpu] = useState<number>(2);
  const [resizeMemory, setResizeMemory] = useState<number>(4);

  const { data: vm, isLoading, error } = useQuery({
    queryKey: ['vm', id],
    queryFn: async () => {
      const response = await vmApi.getVM(id!);
      return response;
    },
    enabled: !!id,
    refetchInterval: 10000, // Poll every 10 seconds
  });

  // Fetch SSH key name if SSH key is configured
  const { data: sshKey } = useQuery({
    queryKey: ['ssh-key', vm?.ssh_key_id],
    queryFn: async () => {
      if (!vm?.ssh_key_id) return null;
      const response = await sshKeyService.get(vm.ssh_key_id);
      return response.data;
    },
    enabled: !!vm?.ssh_key_id,
  });

  // Update VM mutation
  const startMutation = useMutation({
    mutationFn: () => vmApi.startVM(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vm', id] });
      queryClient.invalidateQueries({ queryKey: ['vms'] });
      toast.success('VM starting...');
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      toast.error(error.response?.data?.error || 'Failed to start VM');
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => vmApi.stopVM(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vm', id] });
      queryClient.invalidateQueries({ queryKey: ['vms'] });
      toast.success('VM stopping...');
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      toast.error(error.response?.data?.error || 'Failed to stop VM');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => vmApi.deleteVM(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vms'] });
      toast.success('VM deleted');
      navigate('/vms');
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      toast.error(error.response?.data?.error || 'Failed to delete VM');
    },
  });

  const resizeMutation = useMutation({
    mutationFn: (data: { cpu_cores: number; memory_gb: number }) => vmApi.resizeVM(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vm', id] });
      queryClient.invalidateQueries({ queryKey: ['vms'] });
      toast.success('VM resize initiated');
      setShowResizeModal(false);
    },
    onError: (error: AxiosError<{ error?: string; detail?: string }>) => {
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to resize VM');
    },
  });

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate();
    setShowDeleteConfirm(false);
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Get username from OS image
  const getUsername = (): string => {
    if (vm?.ssh_username) return vm.ssh_username;
    const osLower = vm?.os_image?.toLowerCase() || '';
    if (osLower.startsWith('ubuntu') || osLower.startsWith('tensorflow') || osLower.startsWith('pytorch') || osLower.startsWith('n8n')) {
      return 'ubuntu';
    } else if (osLower.startsWith('debian')) {
      return 'debian';
    } else if (osLower.startsWith('centos') || osLower.startsWith('fedora') || osLower.startsWith('rhel')) {
      return 'root';
    }
    return 'ubuntu';
  };

  // Get SSH command with ProxyCommand
  const getSSHCommand = (): string | null => {
    if (!vm?.public_subdomain) return null;
    const username = getUsername();
    const subdomain = vm.public_subdomain;
    const proxyCommand = `openssl s_client -quiet -connect ${subdomain}:22 -servername ${subdomain}`;
    
    if (vm.ssh_key_id && sshKey?.name) {
      // Use actual SSH key name if available
      const keyFileName = sshKey.name.toLowerCase().replace(/\s+/g, '-') + '.pem';
      return `ssh -i ./${keyFileName} -o ProxyCommand='${proxyCommand}' ${username}@${subdomain}`;
    } else if (vm.ssh_key_id) {
      // Fallback if key name not available
      return `ssh -i ./<SSH-key-name>.pem -o ProxyCommand='${proxyCommand}' ${username}@${subdomain}`;
    } else {
      // No SSH key, password authentication
      return `ssh -o ProxyCommand='${proxyCommand}' ${username}@${subdomain}`;
    }
  };

  // Get SSH config for universal use (wildcard pattern for all Podstack VMs)
  const getSSHConfig = (): string | null => {
    if (!vm?.public_subdomain) return null;
    const username = getUsername();
    
    // Extract domain pattern (e.g., *.cloud.podstack.ai or *.podstack.ai)
    const subdomain = vm.public_subdomain;
    const domainParts = subdomain.split('.');
    // Use wildcard pattern: *.cloud.podstack.ai or *.podstack.ai
    const wildcardHost = domainParts.length >= 3 
      ? `*.${domainParts.slice(-3).join('.')}` // *.cloud.podstack.ai
      : `*.${domainParts.slice(-2).join('.')}`; // *.podstack.ai (fallback)
    
    return `Host ${wildcardHost}
  ProxyCommand openssl s_client -quiet -connect %h:22 -servername %h
  StrictHostKeyChecking no
  User ${username}`;
  };

  // Get simple SSH command with key file path
  const getSimpleSSHCommand = (): string => {
    if (!vm?.public_subdomain) return '';
    const username = getUsername();
    const subdomain = vm.public_subdomain;
    
    if (vm.ssh_key_id && sshKey?.name) {
      const keyFileName = sshKey.name.toLowerCase().replace(/\s+/g, '-') + '.pem';
      return `ssh -i ./${keyFileName} ${username}@${subdomain}`;
    } else if (vm.ssh_key_id) {
      return `ssh -i ./<SSH-key-name>.pem ${username}@${subdomain}`;
    } else {
      return `ssh ${username}@${subdomain}`;
    }
  };

  // Update resize values when VM changes
  useEffect(() => {
    if (vm) {
      setResizeCpu(vm.cpu_cores);
      setResizeMemory(vm.memory_gb);
    }
  }, [vm]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !vm) {
    return (
      <div className="p-6">
        <div className="text-center py-12 bg-gray-900 rounded-xl border border-gray-800">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">VM Not Found</h3>
          <p className="text-gray-400 mb-4">The VM you're looking for doesn't exist or has been deleted.</p>
          <Link to="/vms" className="btn-primary">
            Back to VMs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header
        title={vm.name}
        subtitle="VM Details"
        actions={
          <div className="flex items-center gap-2">
            {vm.status === 'running' ? (
              <button
                onClick={() => setShowStopConfirm(true)}
                disabled={stopMutation.isPending}
                className="btn-secondary flex items-center gap-2"
              >
                <StopIcon className="w-4 h-4" />
                Stop
              </button>
            ) : vm.status === 'stopped' ? (
              <button
                onClick={() => startMutation.mutate()}
                disabled={startMutation.isPending}
                className="btn-primary flex items-center gap-2"
              >
                <PlayIcon className="w-4 h-4" />
                Start
              </button>
            ) : null}
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="btn-danger flex items-center gap-2"
            >
              <TrashIcon className="w-4 h-4" />
              Delete
            </button>
          </div>
        }
      />

      <div className="p-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/vms')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to VMs
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Card */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Status</h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusColors[vm.status] || 'bg-gray-700 text-gray-300 border-gray-600'}`}>
                  {vm.status}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Created</p>
                  <p className="text-sm font-medium text-white">
                    {format(new Date(vm.created_at), 'MMM d, yyyy HH:mm')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">OS Image</p>
                  <p className="text-sm font-medium text-white truncate" title={vm.os_image}>
                    {vm.os_image}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Use Case</p>
                  <p className="text-sm font-medium text-white capitalize">
                    {vm.use_case?.replace('_', ' ') || 'Development/Testing'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Network</p>
                  <p className="text-sm font-medium text-white capitalize">
                    {vm.network_type || 'bridge'}
                  </p>
                </div>
              </div>
              {vm.error_message && (
                <div className="mt-4 p-3 bg-red-500/20 border border-red-600 rounded-lg">
                  <p className="text-sm text-red-400">{vm.error_message}</p>
                </div>
              )}
            </div>

            {/* Web Terminal Card - Shows for all running VMs */}
            {vm.status === 'running' && (
              <div className="card border-2 border-purple-600 bg-purple-500/20">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <CommandLineIcon className="w-5 h-5 text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">Web Terminal</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        const terminalUrl = `/portal/vms/${vm.id}/terminal?project_id=${vm.project_id}`;
                        window.open(terminalUrl, '_blank', 'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no');
                      }}
                      className="btn-secondary btn-sm flex items-center gap-1.5"
                      title="Open terminal in new tab (full-screen experience)"
                    >
                      <ArrowsPointingOutIcon className="w-4 h-4" />
                      Open in New Tab
                    </button>
                    <button
                      onClick={() => setShowTerminal(!showTerminal)}
                      className="btn-primary btn-sm flex items-center gap-1.5"
                    >
                      <CommandLineIcon className="w-4 h-4" />
                      {showTerminal ? 'Hide Terminal' : 'Open Terminal'}
                    </button>
                  </div>
                </div>
                {!showTerminal && (
                  <p className="text-sm text-gray-400">
                    Access an interactive shell in your running VM directly from the browser.
                    Open in a new tab for a full-screen terminal experience.
                  </p>
                )}
                {showTerminal && (
                  <VMWebTerminal
                    vmId={vm.id}
                    projectId={vm.project_id}
                    isRunning={vm.status === 'running'}
                    onClose={() => setShowTerminal(false)}
                  />
                )}
              </div>
            )}

            {/* SSH Access Card */}
            {vm.public_subdomain && (
              <div className="card border-2 border-green-600 bg-green-500/20">
                <div className="flex items-center gap-2 mb-4">
                  <KeyIcon className="w-5 h-5 text-green-400" />
                  <h3 className="text-lg font-semibold text-white">SSH Access</h3>
                </div>
                <div className="space-y-4">
                  {/* SSH Command with ProxyCommand */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      SSH Command (Direct)
                    </label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-gray-900 text-green-400 px-4 py-2 rounded-lg font-mono text-sm break-all">
                        {getSSHCommand()}
                      </code>
                      <button
                        onClick={() => copyToClipboard(getSSHCommand()!, 'ssh-command')}
                        className="p-2 text-gray-400 hover:text-primary-500 hover:bg-gray-800 rounded-lg flex-shrink-0"
                        title="Copy SSH command"
                      >
                        {copiedField === 'ssh-command' ? (
                          <CheckIcon className="w-5 h-5 text-green-400" />
                        ) : (
                          <ClipboardDocumentIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {vm.ssh_key_id 
                        ? `Replace <SSH-key-name> with your SSH private key file name (e.g., ${sshKey?.name ? sshKey.name.toLowerCase().replace(/\s+/g, '-') + '.pem' : 'your-key.pem'})`
                        : 'Password authentication - you will be prompted for password'}
                    </p>
                  </div>

                  {/* SSH Config for Universal Use */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      SSH Config (Universal - Add to ~/.ssh/config)
                    </label>
                    <div className="flex items-start gap-2">
                      <code className="flex-1 bg-gray-900 text-green-400 px-4 py-2 rounded-lg font-mono text-xs whitespace-pre-wrap break-all">
                        {getSSHConfig()}
                      </code>
                      <button
                        onClick={() => copyToClipboard(getSSHConfig()!, 'ssh-config')}
                        className="p-2 text-gray-400 hover:text-primary-500 hover:bg-gray-800 rounded-lg flex-shrink-0 mt-0.5"
                        title="Copy SSH config"
                      >
                        {copiedField === 'ssh-config' ? (
                          <CheckIcon className="w-5 h-5 text-green-400" />
                        ) : (
                          <ClipboardDocumentIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <div className="mt-2 p-3 bg-blue-500/20 border border-blue-600 rounded-lg">
                      <p className="text-xs text-blue-400 font-medium mb-1">After adding to ~/.ssh/config:</p>
                      <p className="text-xs text-blue-300">
                        Simply use: <code className="bg-gray-800 px-1.5 py-0.5 rounded font-mono text-blue-400">{getSimpleSSHCommand()}</code>
                      </p>
                      <p className="text-xs text-blue-400 mt-1">
                        This config works for <strong>all Podstack VMs/Pods</strong> - just copy it once and use the same SSH command format for any VM/Pod.
                      </p>
                    </div>
                  </div>

                  {/* Connection Details */}
                  <div className="pt-3 border-t border-green-600">
                    <p className="text-xs font-medium text-gray-300 mb-2">Connection Details:</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                      <div>
                        <span className="font-medium">Host:</span> <code className="bg-gray-800 px-1 rounded text-gray-300">{vm.public_subdomain}</code>
                      </div>
                      <div>
                        <span className="font-medium">User:</span> <code className="bg-gray-800 px-1 rounded text-gray-300">{getUsername()}</code>
                      </div>
                      <div>
                        <span className="font-medium">Port:</span> <code className="bg-gray-800 px-1 rounded text-gray-300">22</code>
                      </div>
                      <div>
                        <span className="font-medium">Auth:</span> {vm.ssh_key_id ? (
                          <span className="text-green-400 font-medium">SSH Key {sshKey?.name ? `(${sshKey.name})` : ''}</span>
                        ) : (
                          <span className="text-gray-300">Password</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Resources Card */}
            <div className="card">
              <h3 className="text-lg font-semibold text-white mb-4">Resources</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CpuChipIcon className="w-5 h-5 text-blue-400" />
                    <span className="text-sm text-gray-400">CPU</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xl font-semibold text-white">{vm.cpu_cores} cores</p>
                    {(vm.status === 'running' || vm.status === 'stopped') && (
                      <button
                        onClick={() => setShowResizeModal(true)}
                        className="text-xs text-primary-500 hover:text-primary-400 font-medium"
                        title="Resize VM"
                      >
                        Resize
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-4 bg-gray-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <ServerStackIcon className="w-5 h-5 text-purple-400" />
                    <span className="text-sm text-gray-400">Memory</span>
                  </div>
                  <p className="text-xl font-semibold text-white">{vm.memory_gb} GB</p>
                </div>
                <div className="p-4 bg-gray-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CubeIcon className="w-5 h-5 text-orange-400" />
                    <span className="text-sm text-gray-400">Storage</span>
                  </div>
                  <p className="text-xl font-semibold text-white">{vm.storage_gb} GB</p>
                </div>
                {vm.gpu_count > 0 && (
                  <div className="p-4 bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CpuChipIcon className="w-5 h-5 text-green-400" />
                      <span className="text-sm text-gray-400">GPU</span>
                    </div>
                    <p className="text-xl font-semibold text-white">
                      {vm.gpu_count}x
                    </p>
                    {vm.gpu_type && (
                      <p className="text-sm text-gray-400">{vm.gpu_type}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* VM Logs */}
            <VMLogs
              vmId={vm.id}
              projectId={vm.project_id}
              vmName={vm.name}
              isRunning={vm.status === 'running'}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Infrastructure Stats Card */}
            <VMStats
              vmId={vm.id}
              isRunning={vm.status === 'running'}
              allocatedCpu={vm.cpu_cores}
              allocatedMemoryGb={vm.memory_gb}
              allocatedStorageGb={vm.storage_gb}
              gpuCount={vm.gpu_count}
              gpuType={vm.gpu_type}
            />

            {/* Firewall & Ports Card */}
            <VMFirewallPorts
              vmId={vm.id}
              isRunning={vm.status === 'running'}
            />

            {/* VM Info Card */}
            <div className="card">
              <div className="flex items-center gap-2 mb-4">
                <ComputerDesktopIcon className="w-5 h-5 text-blue-400" />
                <h3 className="text-lg font-semibold text-white">VM Information</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-400 mb-1">VM ID</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-gray-800 px-2 py-1 rounded font-mono text-gray-300 truncate">
                      {vm.id}
                    </code>
                    <button
                      onClick={() => copyToClipboard(vm.id, 'vm-id')}
                      className="p-1 text-gray-400 hover:text-primary-500"
                      title="Copy VM ID"
                    >
                      {copiedField === 'vm-id' ? (
                        <CheckIcon className="w-4 h-4 text-green-400" />
                      ) : (
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                {vm.private_ip && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Private IP</p>
                    <p className="text-sm font-medium text-white">{vm.private_ip}</p>
                  </div>
                )}
                {vm.public_subdomain && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Public Subdomain</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-gray-800 px-2 py-1 rounded font-mono text-gray-300 truncate">
                        {vm.public_subdomain}
                      </code>
                      <button
                        onClick={() => copyToClipboard(vm.public_subdomain!, 'subdomain')}
                        className="p-1 text-gray-400 hover:text-primary-500"
                        title="Copy subdomain"
                      >
                        {copiedField === 'subdomain' ? (
                          <CheckIcon className="w-4 h-4 text-green-400" />
                        ) : (
                          <ClipboardDocumentIcon className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                {vm.project_id && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Project ID</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-gray-800 px-2 py-1 rounded font-mono text-gray-300 truncate">
                        {vm.project_id}
                      </code>
                      <button
                        onClick={() => copyToClipboard(vm.project_id, 'project-id')}
                        className="p-1 text-gray-400 hover:text-primary-500"
                        title="Copy Project ID"
                      >
                        {copiedField === 'project-id' ? (
                          <CheckIcon className="w-4 h-4 text-green-400" />
                        ) : (
                          <ClipboardDocumentIcon className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                {vm.description && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Description</p>
                    <p className="text-sm text-gray-300">{vm.description}</p>
                  </div>
                )}
                {vm.is_spot && (
                  <div className="p-2 bg-yellow-500/20 border border-yellow-600 rounded-lg">
                    <p className="text-xs font-medium text-yellow-400">Spot Instance</p>
                    <p className="text-xs text-yellow-300 mt-1">This is a spot VM with potential availability issues.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="relative bg-gray-900 border border-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <TrashIcon className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Delete VM</h3>
            </div>

            <p className="text-gray-300 mb-4">
              Are you sure you want to delete <span className="font-semibold text-white">"{vm?.name}"</span>?
            </p>

            <div className="flex items-start gap-3 p-3 bg-red-500/20 border border-red-600 rounded-lg mb-4">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-400">This action cannot be undone</p>
                <ul className="text-red-300 mt-1 space-y-1">
                  <li>All data stored in this VM will be permanently deleted</li>
                  {vm?.gpu_count > 0 && (
                    <li>The {vm.gpu_type || 'GPU'} will be released back to the pool</li>
                  )}
                </ul>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="btn-danger flex items-center gap-2"
              >
                {deleteMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    Delete VM
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stop Confirmation Modal */}
      {showStopConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowStopConfirm(false)}
          />
          <div className="relative bg-gray-900 border border-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <StopIcon className="w-5 h-5 text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Stop VM</h3>
            </div>

            <p className="text-gray-300 mb-4">
              Are you sure you want to stop <span className="font-semibold text-white">"{vm?.name}"</span>?
            </p>

            {vm?.gpu_count > 0 && (
              <div className="flex items-start gap-3 p-4 bg-amber-500/20 border border-amber-600 rounded-lg mb-4">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-400">GPU Resources Will Be Released</p>
                  <p className="text-sm text-amber-300 mt-1">
                    Stopping this VM will release the <span className="font-semibold">{vm.gpu_type || 'GPU'}</span> back
                    to the shared pool. The GPU will no longer be reserved for this VM.
                  </p>
                </div>
              </div>
            )}

            <div className="bg-gray-800 rounded-lg p-3 mb-6">
              <p className="text-sm text-gray-300">
                <span className="font-medium">Note:</span> Billing will be paused while the VM is stopped.
                Your data and configuration will be preserved.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowStopConfirm(false)}
                className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  stopMutation.mutate();
                  setShowStopConfirm(false);
                }}
                disabled={stopMutation.isPending}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2 font-medium"
              >
                {stopMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Stopping...
                  </>
                ) : (
                  <>
                    <StopIcon className="w-4 h-4" />
                    Confirm Stop
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resize VM Modal */}
      {showResizeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowResizeModal(false)}
          />
          <div className="relative bg-gray-900 border border-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <CpuChipIcon className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Resize VM</h3>
            </div>

            <p className="text-gray-300 mb-6">
              Update CPU and memory for <span className="font-semibold text-white">"{vm?.name}"</span>
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  CPU Cores
                </label>
                <input
                  type="number"
                  min="1"
                  value={resizeCpu}
                  onChange={(e) => setResizeCpu(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-400 mt-1">Current: {vm?.cpu_cores} cores</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Memory (GB)
                </label>
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={resizeMemory}
                  onChange={(e) => setResizeMemory(parseFloat(e.target.value) || 0.5)}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-400 mt-1">Current: {vm?.memory_gb} GB</p>
              </div>
            </div>

            <div className="bg-blue-500/20 border border-blue-600 rounded-lg p-3 mb-6">
              <p className="text-sm text-blue-400">
                <span className="font-medium">Note:</span> The VM will be updated with the new resources. 
                {vm?.status === 'running' && ' The VM may need to restart to apply changes.'}
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResizeModal(false)}
                className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (resizeCpu >= 1 && resizeMemory >= 0.5) {
                    resizeMutation.mutate({ cpu_cores: resizeCpu, memory_gb: resizeMemory });
                  } else {
                    toast.error('CPU must be at least 1 and memory must be at least 0.5 GB');
                  }
                }}
                disabled={resizeMutation.isPending || resizeCpu < 1 || resizeMemory < 0.5}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 font-medium"
              >
                {resizeMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Resizing...
                  </>
                ) : (
                  <>
                    <CpuChipIcon className="w-4 h-4" />
                    Resize VM
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VMDetail;
